import React, { CSSProperties, forwardRef, useMemo, useRef, useImperativeHandle } from "react";
import { Badge as PrimeBadge, BadgeProps as PrimeBadgeProps } from "primereact/badge";
import { getColorBaseTokenValue, mergeClassNames } from "../../design-tokens";
import styles from "./Badge.module.css";

/**
 * Variantes visuais do badge
 * - dot: Badge pequeno sem valor (ponto de notificação)
 * - value: Badge com número/texto (padrão)
 */
export type BadgeVariant = "dot" | "value";

/**
 * Props do componente Badge
 */
export interface BadgeProps
  extends Omit<PrimeBadgeProps, "severity" | "size" | "pt" | "ptOptions"> {
  /**
   * Valor numérico exibido no badge
   * - Números > 99 exibem "99+"
   * - Se 0, badge não é renderizado (exceto variant="dot")
   */
  value?: number;

  /**
   * Variante visual
   * @default "value"
   */
  variant?: BadgeVariant;
}

/**
 * Cria variáveis CSS para estilização
 */
const createStyleVariables = (): CSSProperties => {
  const bgColor = getColorBaseTokenValue("color.feedback.danger.700");
  const textColor = getColorBaseTokenValue("color.neutral.000");

  return {
    "--ds-badge-bg": bgColor,
    "--ds-badge-color": textColor
  } as CSSProperties;
};

/**
 * Formata valor para exibição
 * - Números > 99 viram "99+"
 */
const formatValue = (value?: number): string | number | undefined => {
  if (value !== undefined && value > 99) {
    return "99+";
  }
  return value;
};

/**
 * Badge Component
 * 
 * Indicador visual compacto para contadores, status e notificações.
 * Pode ser usado standalone ou sobreposto a outros elementos (overlay).
 * 
 * @example
 * // Badge simples
 * <Badge value={5} severity="danger" />
 * 
 * @example
 * // Badge em botão (overlay)
 * <div className="p-overlay-badge">
 *   <Button icon="notifications" />
 *   <Badge value={3} severity="danger" />
 * </div>
 * 
 * @example
 * // Badge dot (sem valor)
 * <Badge variant="dot" severity="success" />
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      value,
      variant = "value",
      className,
      style,
      ...rest
    },
    ref
  ) => {
    // Ref interno para capturar o elemento DOM do PrimeBadge
    const badgeRef = useRef<any>(null);

    // Expõe o elemento DOM através do ref externo
    useImperativeHandle(ref, () => {
      return badgeRef.current?.getElement?.() || badgeRef.current;
    }, []);

    // Formata valor
    const formattedValue = variant === "dot" ? undefined : formatValue(value);

    // Monta classes CSS
    const classNames = mergeClassNames(
      styles.root,
      styles[variant],
      className
    );

    // Cria variáveis CSS
    const styleVariables = useMemo<CSSProperties>(
      () => ({
        ...createStyleVariables(),
        ...style
      }),
      [style]
    );

    // Não renderiza se value for 0 e variant for "value"
    if (variant === "value" && value === 0) {
      return null;
    }

    return (
      <PrimeBadge
        {...rest}
        ref={badgeRef}
        value={formattedValue}
        className={classNames}
        style={styleVariables}
        data-ds-variant={variant}
      />
    );
  }
);

Badge.displayName = "Badge";

export default Badge;
